import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/4938a3ae/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import ModalStateManager from "../../../components/ModalStateManager";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentDemo = makeShortcode("ComponentDemo");
const InlineNotification = makeShortcode("InlineNotification");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h2>{`Modal`}</h2>
    <p><inlineCode parentName="p">{`<Modal>`}</inlineCode>{` allows you to use its bespoke set of contents. `}<inlineCode parentName="p">{`children`}</inlineCode>{` prop maps to the modal body content.
You can also use `}<inlineCode parentName="p">{`modalLabel`}</inlineCode>{`, `}<inlineCode parentName="p">{`modalHeading`}</inlineCode>{`, `}<inlineCode parentName="p">{`secondaryButtonText`}</inlineCode>{` and `}<inlineCode parentName="p">{`primaryButtonText`}</inlineCode>{` props to change the corresponding text:`}</p>
    <ComponentDemo scope={{
      ModalStateManager,
      React
    }} knobs={{
      Modal: ["passiveModal", "danger", "hasScrollingContent", "size"]
    }} links={{
      React: "https://react.carbondesignsystem.com/?path=/story/modal--default",
      Angular: "https://angular.carbondesignsystem.com/?path=/story/components-modal--basic",
      Vue: "http://vue.carbondesignsystem.com/?path=/story/components-cvmodal--default",
      Vanilla: "https://the-carbon-components.netlify.com/?nav=modal"
    }} mdxType="ComponentDemo">
  {`
<>
  {/*
    <ModalStateManager> is something application writes to implement a launcher button with its state.
    See "Opening/closing modal" section for more details.
  */}
  <ModalStateManager
    renderLauncher={({ open, setOpen }) => (
      <Button onClick={() => setOpen(true)}>Launch modal</Button>
    )}
  >
    {({ open, setOpen }) => (
      <Modal
        buttonTriggerText="Launch modal"
        modalHeading="Modal heading"
        modalLabel="Label"
        primaryButtonText="OK"
        secondaryButtonText="Cancel"
        open={open}
        onRequestClose={() => setOpen(false)}
      >
        <p> Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse cursus fermentum risus, sit amet fringilla nunc pellentesque quis. Duis quis odio ultrices, cursus lacus ac, posuere felis. Donec dignissim libero in augue mattis, a molestie metus vestibulum. Aliquam placerat felis ultrices lorem condimentum, nec ullamcorper felis porttitor. </p>
      </Modal>
    )}
  </ModalStateManager>
</>
`}
    </ComponentDemo>
    <h2>{`ComposedModal`}</h2>
    <p><inlineCode parentName="p">{`<ComposedModal>`}</inlineCode>{` provides you with more flexibility in the contents. You can provide the contents via `}<inlineCode parentName="p">{`<ModalHeader>`}</inlineCode>{`, `}<inlineCode parentName="p">{`<ModalBody>`}</inlineCode>{` and `}<inlineCode parentName="p">{`<ModalFooter>`}</inlineCode>{` with your contents in them:`}</p>
    <ComponentDemo scope={{
      ModalStateManager,
      React
    }} knobs={{
      ComposedModal: ["size"],
      ModalBody: ["hasScrollingContent"],
      ModalFooter: ["danger"]
    }} links={{
      React: "https://react.carbondesignsystem.com/?path=/story/composedmodal--using-child-nodes"
    }} mdxType="ComponentDemo">
  {`
<>
  {/*
    <ModalStateManager> is something application writes to implement a launcher button with its state.
    See "Opening/closing modal" section for more details.
  */}
  <ModalStateManager
    renderLauncher={({ open, setOpen }) => (
      <Button onClick={() => setOpen(true)}>Launch modal</Button>
    )}
  >
    {({ open, setOpen }) => (
      <ComposedModal open={open} onClose={() => setOpen(false)}>
        <ModalHeader label="Modal label">
          <h1>Modal heading</h1>
        </ModalHeader>
        <ModalBody>
          <p className=".bx--modal-content__text">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse cursus fermentum risus, sit amet fringilla nunc pellentesque quis. Duis quis odio ultrices, cursus lacus ac, posuere felis. Donec dignissim libero in augue mattis, a molestie metus vestibulum. Aliquam placerat felis ultrices lorem condimentum, nec ullamcorper felis porttitor.
          </p>
        </ModalBody>
        <ModalFooter primaryButtonText="OK" secondaryButtonText="Cancel" />
      </ComposedModal>
    )}
  </ModalStateManager>
</>
`}
    </ComponentDemo>
    <h2>{`Installation`}</h2>
    <p>{`To use styles:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scss"
      }}>{`@import "carbon-components/scss/components/modal/modal";
`}</code></pre>
    <p>{`To import React components:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// For using \`<Modal>\`
import { Modal } from "carbon-components-react";

// For using \`<ComposedModal>\`
import {
  ComposedModal,
  ModalHeder,
  ModalBody,
  ModalFooter,
} from "carbon-components-react";
`}</code></pre>
    <h2>{`Usage`}</h2>
    <p>{`This section explains usage with our React variant. For usage with other frameworks, please follow a link in component live demo above.`}</p>
    <h3>{`Opening/closing modal`}</h3>
    <p>{`For both modal variants, you can open/close the modal by changing the `}<inlineCode parentName="p">{`open`}</inlineCode>{` prop. For example, you can implement a launcher button with a React state and a `}<inlineCode parentName="p">{`<Button>`}</inlineCode>{` that changes the state:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function ModalStateManager() {
  const [open, setOpen] = useState(false);
  return (
    <>
      {typeof document === "undefined"
        ? null
        : ReactDOM.createPortal(
            <ComposedModal open={open} onClose={() => setOpen(false)}>
              ...
            </ComposedModal>,
            document.body
          )}
      <Button kind="primary" onClick={() => setOpen(true)}>
        Open modal
      </Button>
    </>
  );
}
`}</code></pre>
    <p>{`You can create an abstract version of such state manager, like:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const ModalStateManager = ({
  renderLauncher: LauncherContent,
  children: ModalContent,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {!ModalContent || typeof document === "undefined"
        ? null
        : ReactDOM.createPortal(
            <ModalContent open={open} setOpen={setOpen} />,
            document.body
          )}
      {LauncherContent && <LauncherContent open={open} setOpen={setOpen} />}
    </>
  );
};
`}</code></pre>
    <InlineNotification kind="warning" mdxType="InlineNotification">
  <code>{`<Modal>`}</code>/<code>{`<ComposedModal>`}</code> has to be put at the
  top-level in a React tree. The easiest way to ensure that is using React
  portal as shown in above example.
    </InlineNotification>
    <h3>{`Modal sizes`}</h3>
    <p>{`There are four responsive `}<a href="https://www.carbondesignsystem.com/components/modal/usage/#modal-sizes">{`modal sizes`}</a>{`: `}<inlineCode parentName="p">{`xs`}</inlineCode>{`, `}<inlineCode parentName="p">{`small`}</inlineCode>{`, default, and `}<inlineCode parentName="p">{`large`}</inlineCode>{`. You can set it via `}<inlineCode parentName="p">{`size`}</inlineCode>{` prop:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<ComposedModal size="lg">
  <ModalHeader />
  <ModalBody>
    <p className="bx--modal-content__text">
      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
      cursus fermentum risus, sit amet fringilla nunc pellentesque quis. Duis
      quis odio ultrices, cursus lacus ac, posuere felis. Donec dignissim libero
      in augue mattis, a molestie metus vestibulum. Aliquam placerat felis
      ultrices lorem condimentum, nec ullamcorper felis porttitor.
    </p>
  </ModalBody>
</ComposedModal>
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
  <h5>Form elements in modal body</h5>
  Depending on the modal size of your choice and the viewport size, <code>{`<Modal>`}</code>
      <p>{`/`}<code>{`{`}<inlineCode parentName="p">{`<ComposedModal>`}</inlineCode>{`}`}</code>{` adds 20% padding at the right of the modal
body content. Carbon design specifies that such 20% padding shouldn’t be
applied to form elements. You can set `}<code>{`hasForm`}</code>{`
prop to `}<code>{`{`}<inlineCode parentName="p">{`<Modal>`}</inlineCode>{`}`}</code>{`/`}<code>{`{`}<inlineCode parentName="p">{`<ModalBody>`}</inlineCode>{`}`}</code>{` to remove the padding,
and use`}</p>
  <code>bx--modal-content__regular-content</code> class to apply the 20% padding
  to non-form contents, like below:
    </InlineNotification>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<ComposedModal>
  <ModalHeader />
  <ModalBody hasForm>
    <TextInput data-modal-primary-focus labelText="Enter something" />
    <p className="bx--modal-content__text bx--modal-content__regular-content">
      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
      cursus fermentum risus, sit amet fringilla nunc pellentesque quis. Duis
      quis odio ultrices, cursus lacus ac, posuere felis. Donec dignissim libero
      in augue mattis, a molestie metus vestibulum. Aliquam placerat felis
      ultrices lorem condimentum, nec ullamcorper felis porttitor.
    </p>
  </ModalBody>
</ComposedModal>
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
  <b>Scrolling behavior</b>: In case where the largest modal size cannot fit
  your modal body content in, Carbon design specifies to have a "visual fade"
  look at the end of the modal body area to indicate there is additional content
  out of view. You can set <code>hasScrollingContent</code> prop to
  <code>{`<Modal>`}</code>/<code>{`<ModalBody>`}</code> to do that, like below:
    </InlineNotification>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<ComposedModal size="large">
  <ModalHeader />
  <ModalBody hasScrollingContent>
    <p className=".bx--modal-content__text">Some very large contents...</p>
  </ModalBody>
</ComposedModal>
`}</code></pre>
    <h3>{`Modal button variants`}</h3>
    <p>{`With `}<inlineCode parentName="p">{`<Modal>`}</inlineCode>{`, you have limited control over the set of buttons. Supported patterns are the following:`}</p>
    <ul>
      <li parentName="ul">{`No button (`}<a parentName="li" {...{
          "href": "https://www.carbondesignsystem.com/components/modal/usage#passive-modal"
        }}>{`passive modal`}</a>{`): With `}<inlineCode parentName="li">{`passiveModal`}</inlineCode>{` prop in `}<inlineCode parentName="li">{`<Modal>`}</inlineCode></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.carbondesignsystem.com/components/modal/usage#transactional-modal"
        }}>{`Transactional modals`}</a>{`: Only supports two buttons variant. For others, use `}<inlineCode parentName="li">{`<ComposedModal>`}</inlineCode></li>
      <li parentName="ul">{`Danger modal: With `}<inlineCode parentName="li">{`danger`}</inlineCode>{` prop in `}<inlineCode parentName="li">{`<Modal>`}</inlineCode>{` (like below)`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<Modal danger>
  <ModalHeader />
  <p className="bx--modal-content__text">The modal body content</p>
</Modal>
`}</code></pre>
    <p>{`With `}<inlineCode parentName="p">{`<ComposedModal>`}</inlineCode>{`, you can control the buttons with the following:`}</p>
    <ul>
      <li parentName="ul">{`No button (`}<a parentName="li" {...{
          "href": "https://www.carbondesignsystem.com/components/modal/usage#passive-modal"
        }}>{`passive modal`}</a>{`): By not putting `}<inlineCode parentName="li">{`<ModalFooter>`}</inlineCode></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.carbondesignsystem.com/components/modal/usage#transactional-modal"
        }}>{`Transactional modals`}</a>{`:`}<ul parentName="li">
          <li parentName="ul">{`One button (`}<a parentName="li" {...{
              "href": "https://www.carbondesignsystem.com/components/modal/usage#acknowledgment-modal"
            }}>{`acknowledgement modal`}</a>{`): By setting `}<inlineCode parentName="li">{`primaryButtonText`}</inlineCode>{` prop to `}<inlineCode parentName="li">{`<ModalFooter>`}</inlineCode></li>
          <li parentName="ul">{`Two buttons: By setting `}<inlineCode parentName="li">{`secondaryButtonText`}</inlineCode>{` prop to `}<inlineCode parentName="li">{`<ModalFooter>`}</inlineCode>{` in addition to `}<inlineCode parentName="li">{`primaryButtonText`}</inlineCode>{` prop`}</li>
          <li parentName="ul">{`Three buttons: Put three buttons as children of `}<inlineCode parentName="li">{`<ModalFooter>`}</inlineCode>{`, instead of using `}<inlineCode parentName="li">{`primaryButtonText`}</inlineCode>{` or `}<inlineCode parentName="li">{`secondaryButtonText`}</inlineCode>{`. Using this option requires style adjustment defined in application-level CSS for `}<inlineCode parentName="li">{`<ModalFooter>`}</inlineCode>{`, e.g. `}<inlineCode parentName="li">{`.bx--modal-footer { padding: 25%; }`}</inlineCode>{`.`}</li>
        </ul></li>
      <li parentName="ul">{`Danger modal: By setting `}<inlineCode parentName="li">{`danger`}</inlineCode>{` prop in `}<inlineCode parentName="li">{`<ModalFooter>`}</inlineCode>{` (like below)`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<ComposedModal>
  ...
  <ModalFooter danger primaryButtonText="OK" secondaryButtonText="Cancel" />
</ComposedModal>
`}</code></pre>
    <InlineNotification kind="warning" mdxType="InlineNotification">
  As the instruction for the three buttons implies, <code>{`<ModalFooter>`}</code>{" "}
  is flexible on the buttons as you render <code>{`<Button>`}</code> s by
  yourself, like below. In this case, application code needs to care of running
  action upon those buttons, e.g. closing the modal:
    </InlineNotification>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<ComposedModal>
  ...
  <ModalFooter>
    <Button
      kind="secondary"
      onClick={() => { (Run some action...) setOpen(false); }}>
      Another button
    </Buttton>
    <Button
      kind="secondary"
      onClick={() => { (Run some action...) setOpen(false); }}>
      Secondary button
    </Buttton>
    <Button
      kind="primary"
      onClick={() => { (Run some action...) setOpen(false); }}>
      Priamry button
    </Buttton>
  </ModalFooter>
</ComposedModal>
`}</code></pre>
    <h3>{`Using modal title as message`}</h3>
    <p>{`For short, direct messages the title can include the whole message to add visual clarity to an otherwise repetitive title and body message.`}</p>
    <p>{`To use this pattern with `}<inlineCode parentName="p">{`<Modal>`}</inlineCode>{`, you can omit `}<inlineCode parentName="p">{`children`}</inlineCode>{` prop:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const modalHeadding =
  'Are you sure you want to add the "Speech to Text" service ' +
  'to the node-test app?';
...
<Modal
  modalHeading={modalHeading}
  secondaryButtonText="Cancel"
  primaryButtonText="Add"
/>
`}</code></pre>
    <p>{`To use this pattern with `}<inlineCode parentName="p">{`<ComposedModal>`}</inlineCode>{`, you can omit `}<inlineCode parentName="p">{`<ModalBody>`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<ComposedModal>
  <ModalHeader label="Modla label">
    <h1>
      Are you sure you want to add the "Speech to Text" service to the node-test
      app?
    </h1>
  </ModalHeader>
  <ModalFooter primaryButtonText="OK" secondaryButtonText="Cancel" />
</ComposedModal>
`}</code></pre>
    <h3>{`Focus management`}</h3>
    <p>{`For both modal variants, once modal gets open, keyboard focus will be restricted inside modal. It means:`}</p>
    <ul>
      <li parentName="ul">{`If you hit tab key at the last keyboard-focusable element in modal, the first keyboard-focusable element in modal will get focus.`}</li>
      <li parentName="ul">{`If you hit shift-tab key at the first keyboard-focusable element in modal, the last keyboard-focusable element in modal will get focus.`}</li>
    </ul>
    <InlineNotification mdxType="InlineNotification">
  We take extra step here to ensure such behavior works with floating menus,
  given floating menu is placed outside of modal in DOM. If you use any
  non-Carbon floating menus in your application, set{" "}
  <code>
    selectorsFloatingMenus=
    {[".bx--overflow-menu-options", ".bx--tooltip", ".flatpickr-calendar", ".your-floating-menu-foo", ".your-floating-menu-bar"]}`
  </code>{" "}
  to <code>{`<Modal>`}</code>/<code>{`<ComposedModal>`}</code>.
    </InlineNotification>
    <p>{`Also for both modal variants, you can set the DOM element that gets focus when the modal gets open, by either of the following ways:`}</p>
    <h4>{`Setting `}<inlineCode parentName="h4">{`data-modal-primary-focus`}</inlineCode>{` attribute to the target element`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<ComposedModal>
  <ModalBody hasForm>
    <TextInput data-modal-primary-focus labelText="Enter something" />
  </ModalBody>
</ComposedModal>
`}</code></pre>
    <h4>{`Specifying a query selector to the target element`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
  /* \`.bx--text-input\` selects the \`<input>\` in \`<TextInput>\` */
}
<ComposedModal selectorPrimaryFocus=".bx--text-input">
  <ModalBody hasForm>
    <TextInput labelText="Enter something" />
  </ModalBody>
</ComposedModal>;
`}</code></pre>
    <h2>{`Component API`}</h2>
    <p>{`Please see Show Info button at `}<a parentName="p" {...{
        "href": "https://react.carbondesignsystem.com/?path=/story/modal--default"
      }}>{`our storybook`}</a>{`, or follow a link in component live demo above for other frameworks.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      